// app/javascript/controllers/file_upload_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "fileName", "preview", "progressBar", "progressContainer", "fileContainer"]
  static values = {
    allowedTypes: Array,
    maxSize: Number  // en octets
  }

  connect() {
    this.reset()
    
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener("direct-upload:initialize", this.initializeUpload.bind(this))
      this.inputTarget.addEventListener("direct-upload:start", this.startUpload.bind(this))
      this.inputTarget.addEventListener("direct-upload:progress", this.progressUpload.bind(this))
      this.inputTarget.addEventListener("direct-upload:error", this.errorUpload.bind(this))
      this.inputTarget.addEventListener("direct-upload:end", this.endUpload.bind(this))
    }
  }
  
  // Quand un fichier est sélectionné
  fileSelected() {
    if (!this.hasInputTarget || this.inputTarget.files.length === 0) {
      this.reset()
      return
    }
    
    const file = this.inputTarget.files[0]
    
    // Valider le type de fichier
    if (this.hasAllowedTypesValue && this.allowedTypesValue.length > 0) {
      const fileType = file.type
      const fileExtension = file.name.split('.').pop().toLowerCase()
      
      const isAllowedType = this.allowedTypesValue.some(type => {
        return fileType.includes(type) || 
               (type.startsWith('.') && `.${fileExtension}` === type)
      })
      
      if (!isAllowedType) {
        this.showError(`Type de fichier non autorisé. Types acceptés: ${this.allowedTypesValue.join(', ')}`)
        this.reset()
        return
      }
    }
    
    // Valider la taille
    if (this.hasMaxSizeValue && file.size > this.maxSizeValue) {
      const maxSizeMB = this.maxSizeValue / (1024 * 1024)
      this.showError(`Fichier trop volumineux. Taille maximum: ${maxSizeMB.toFixed(2)} MB`)
      this.reset()
      return
    }
    
    // Afficher le nom du fichier
    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = file.name
      this.fileNameTarget.classList.remove('hidden')
    }
    
    // Afficher une prévisualisation pour les images
    if (this.hasPreviewTarget && file.type.includes('image')) {
      const reader = new FileReader()
      reader.onload = (e) => {
        this.previewTarget.src = e.target.result
        this.previewTarget.classList.remove('hidden')
      }
      reader.readAsDataURL(file)
    }
    
    if (this.hasFileContainerTarget) {
      this.fileContainerTarget.classList.add('border-blue-500')
      this.fileContainerTarget.classList.remove('border-gray-300')
    }
  }
  
  // Quand l'upload est initialisé
  initializeUpload(event) {
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.remove('hidden')
    }
  }
  
  // Quand l'upload commence
  startUpload(event) {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = '0%'
    }
  }
  
  // Pendant l'upload (mise à jour de la barre de progression)
  progressUpload(event) {
    const { progress } = event.detail
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${progress}%`
    }
  }
  
  // En cas d'erreur
  errorUpload(event) {
    const { error } = event.detail
    this.showError(`Erreur d'upload: ${error}`)
  }
  
  // Quand l'upload est terminé
  endUpload(event) {
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.add('hidden')
    }
  }
  
  // Réinitialiser le champ
  reset() {
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }
    
    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = ""
      this.fileNameTarget.classList.add('hidden')
    }
    
    if (this.hasPreviewTarget) {
      this.previewTarget.src = ""
      this.previewTarget.classList.add('hidden')
    }
    
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = '0%'
    }
    
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.add('hidden')
    }
    
    if (this.hasFileContainerTarget) {
      this.fileContainerTarget.classList.remove('border-blue-500')
      this.fileContainerTarget.classList.add('border-gray-300')
    }
  }
  
  // Afficher un message d'erreur
  showError(message) {
    console.error(message)
    // Vous pouvez ajouter ici un code pour afficher l'erreur à l'utilisateur
    // Par exemple en ajoutant un élément au DOM ou en utilisant une bibliothèque de notification
  }
}